import React, { useState, useEffect, useRef, useMemo } from 'react';
import config from '../../../utils/config';
import { useAuth } from '../../../wms/context/wms-context';
import { getAllScanners } from '../../../utils/commonAPICalls.js';
import Notification from '../../../components/Notification';
import logError from '../../../utils/errorUtil';

export default function ControlHeader({ status, setStatus, selectedMaid, setSelectedMaid }) {
    const ORIGIN = config.origin;
    const { currentUser, setCurrentUser } = useAuth();

    const [selectedScanner, setSelectedScanner] = useState('');

    const [allMaids, setAllMaids] = useState([]);

    const [allScanners, setAllScanners] = useState([]);

    const [maidLines, setMaidLines] = useState();
    const [showMaidLines, setShowMaidLines] = useState(true);

    const [showNotification, setShowNotification] = useState(false);
    const [notificationDetails, setNotificationDetails] = useState({});
    const [showCompleted, setShowCompleted] = useState(false);

    const isLineCompleted = (line) => {
        const rawStatus = line?.inscor__Status__c;

        if (!rawStatus) return false;

        const status = rawStatus.toString().trim().toLowerCase();

        return ['complete'].includes(status);
    };

    const partFilterRef = useRef();

    // Ensure that the selected scanner updates even when changed from the modal
    useEffect(() => {
        if (localStorage.getItem('selectedScanner') !== null) {
            const savedScannerString = localStorage.getItem('selectedScanner');
            const savedScanner = savedScannerString && JSON.parse(savedScannerString);
            setSelectedScanner(savedScanner);
        }
    }, [currentUser]);

    useEffect(() => {
        const savedScannerString = localStorage.getItem('selectedScanner');
        const savedScanner = savedScannerString && JSON.parse(savedScannerString);

        if (savedScanner && savedScanner.name) {
            setSelectedScanner(savedScanner);
            setAllScanners([savedScanner, { name: '', displayName: 'LOADING SCANNERS' }]);
            setStatus({ ...status, loading: false, receivingPanel: false });
            currentUser.selectedScanner = savedScanner;
        }

        if (!allScanners?.length) {
            getAllScanners(currentUser, setCurrentUser).then((data) => {
                if (data?.error) {
                    setStatus({ ...status, loading: false, receivingPanel: false, error: true, errorMessage: data.message });
                    return [];
                }
                if (data === 'Unauthorized') {
                    setNotificationDetails({
                        title: 'Error',

                        desc: 'You are not authorized to view this page.',
                        status: 'error',
                    });

                    setShowNotification(true);
                    setStatus({ ...status, loading: false, receivingPanel: false });
                    return;
                }

                setAllScanners(data);
            });
        }
    }, []);

    useEffect(() => {
        const savedMaidString = localStorage.getItem('selectedMaid');
        const savedMaid = savedMaidString && JSON.parse(savedMaidString);

        let initialMaid = null;

        if (savedMaid && savedMaid.Id) {
            initialMaid = savedMaid;
            setSelectedMaid(savedMaid);
            setAllMaids([savedMaid, { Id: '', Name: 'LOADING MANIFESTS' }]);
            setStatus({ ...status, loading: false, receivingPanel: false });
            currentUser.selectedMaid = savedMaid;
        }

        if (!allMaids?.length) getAllMaids();
    }, []);

    useEffect(() => {
        if (selectedMaid && allMaids) {
            // find the maid in the allMaids array
            const foundMaid = allMaids.find((maid) => maid.Id === selectedMaid);
            if (foundMaid) {
                setSelectedMaid(foundMaid.Id);
            }
        }
    }, [allMaids, selectedMaid]);

    const handleMaidChange = (event) => {
        const value = event.target.value;

        if (value !== 'multiple') {
            // Find the maid in the allMaids array to get both ID and Name
            const maidData = allMaids.find((maid) => maid.Id === value);
            if (maidData) {
                localStorage.setItem('selectedMaid', JSON.stringify(maidData));
                currentUser.selectedMaid = maidData;
                setSelectedMaid(maidData);
            }
        } else {
            const maidData = {
                Id: 'multiple',
                Name: 'Multiple Manifests',
                inscor__Manifest_Name__c: null,
                inscor__Purchase_Order__c: null,
                inscor__Purchase_Order__rName: null,
                inscor__Repair_Order__c: null,
                inscor__Repair_Order__rName: null,
                inscor__Manifest_Receiving_Options__c: null,
                inscor__Status__c: null,
                inscor__Inventory__c: null,
                inscor__Protected__c: null,
            };

            localStorage.setItem('selectedMaid', JSON.stringify(maidData));
            currentUser.selectedMaid = maidData;
            setSelectedMaid(maidData);
        }
    };

    const handleScannerChange = (event) => {
        const value = event.target.value;
        setSelectedScanner(value);

        // Find the scanner in the allScanners array to get both ID and Name
        const scannerData = allScanners.find((scanner) => scanner.name === value);
        if (scannerData) {
            localStorage.setItem(
                'selectedScanner',
                JSON.stringify({ displayName: scannerData.displayName, name: scannerData.name })
            );
            setSelectedScanner(scannerData);
            setCurrentUser({ ...currentUser, selectedScanner: scannerData });
        }
    };

    const handlePartFilter = () => {
        // check if scanner and MAID are selected
        if (!selectedScanner || !selectedScanner.name || !selectedMaid || !selectedMaid.Id) {
            setNotificationDetails({
                title: 'Error',
                desc: 'You must select a scanner and a manifest before filtering by part number.',
                status: 'error',
            });
            setShowNotification(true);
            return;
        }

        setStatus({ ...status, loading: true, receivingPanel: false, receiveNewLinePanel: false });

        // remove any % ' or " from the part number

        partFilterRef.current.value = partFilterRef.current.value.replace(/[%'"]/g, '');

        const dataToSend = {
            manifestId: selectedMaid.Id,
            filterValue: partFilterRef.current.value,
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
        };

        fetch(`${ORIGIN}/wms/receiving/get-manifest-lines`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
        })
            .then((res) => {
                if (res.ok && res.headers.get('content-type')?.includes('application/json')) {
                    return res.json();
                } else {
                    return {
                        error: 'No Manifest Id',
                    };
                }
            })
            .then((data) => {
                if (data?.error === 'No Manifest Id') {
                    setNotificationDetails({
                        title: 'No Manifest ID',
                        desc: 'Please log out and log back in.',
                        status: 'error',
                    });
                    setShowNotification(true);
                    setStatus({ ...status, loading: false, receivingPanel: false });
                    return;
                }
                // if status 401 unauthorized, show notification and return
                if (data === 'Unauthorized') {
                    setNotificationDetails({
                        title: 'Error',
                        desc: 'You are not authorized to view this page.',
                        status: 'error',
                    });
                    setShowNotification(true);
                    setStatus({ ...status, loading: false, receivingPanel: false });
                    return;
                }

                setStatus({ ...status, loading: false, receivingPanel: false });
                setShowMaidLines(true);
                setMaidLines(data);
            })
            .catch((err) => {
                logError(err, 'handlePartFilter');
                setStatus({ ...status, loading: false, error: true });
            });
    };

    const getAllMaids = () => {
        const dataToSend = {
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
        };
        fetch(`${ORIGIN}/wms/receiving/get-manifests`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
        })
            .then((res) => {
                if (!res.ok) {
                    if (res.status === 500) {
                        // Process the response to get the error message
                        return res.json().then((errorData) => {
                            setStatus({
                                ...status,
                                loading: false,
                                error: true,
                                errorMessage: errorData.message || 'Internal Server Error',
                            });
                        });
                    }
                    // Handle other non-2xx responses if needed
                }
                if (res.ok && res.headers.get('content-type')?.includes('application/json')) {
                    return res.json();
                } else {
                    return {
                        error: 'Internal Server Error',
                    };
                }
            })
            .then((data) => {
                if (data?.error === 'Internal Server Error') {
                    setNotificationDetails({
                        title: 'Internal Server Error',
                        desc: 'Please log out and log back in.',
                        status: 'error',
                    });
                    setShowNotification(true);
                    setStatus({ ...status, loading: false, receivingPanel: false });
                    return;
                }

                const savedMaidString = localStorage.getItem('selectedMaid');
                const savedMaid = savedMaidString && JSON.parse(savedMaidString);

                if (savedMaid && savedMaid.Id) {
                    const stillExists = data.some((maid) => maid.Id === savedMaid.Id);

                    if (!stillExists) {
                        if (savedMaid.Id !== 'multiple') {
                            localStorage.removeItem('selectedMaid');
                            setSelectedMaid({});
                            currentUser.selectedMaid = null;
                        }
                    } else {
                        const freshMaid = data.find((m) => m.Id === savedMaid.Id);
                        if (freshMaid) {
                            setSelectedMaid(freshMaid);
                            currentUser.selectedMaid = freshMaid;
                            localStorage.setItem('selectedMaid', JSON.stringify(freshMaid));
                        }
                    }
                }

                setAllMaids(data);
            })
            .catch((err) => {
                logError(err, 'getAllMaids');
                return [];
            });
    };

    const handleMaidLineSelection = (line) => {
        setShowMaidLines(false);
        setStatus({ ...status, receivingPanel: true, partNumber: line?.inscor__Part_Master__rName || '' });
        currentUser.selectedMaidLine = line;
    };

    const openReceivingLine = () => {
        if (!selectedScanner || !selectedScanner.name || !selectedMaid || !selectedMaid.Id) {
            setNotificationDetails({
                title: 'Error',
                desc: 'You must select a scanner and a manifest.',
                status: 'error',
            });
            setShowNotification(true);
            return;
        }
        if (status.loading) return;

        // Pre-check Manifest Name for the single-manifest flow so we never flash the modal open.
        // The multiple-manifest flow selects its manifest inside the modal, so it's handled there instead.
        if (selectedMaid.Id !== 'multiple' && !selectedMaid.inscor__Manifest_Name__c) {
            setNotificationDetails({
                title: 'Manifest Error',
                desc: 'Manifests "Manifest Name" field is not filled out. Please fill this out in order to receive against this MAID.',
                status: 'error',
            });
            setShowNotification(true);
            return;
        }

        setCurrentUser({ ...currentUser, selectedManifest: selectedMaid });
        setStatus({ ...status, receiveNewLinePanel: true, receivingPanel: false, actionPanel: false, loading: false });
        setShowMaidLines(false);
    };

    return (
        <>
            <Notification
                showSavedNotification={showNotification}
                setShowSavedNotification={setShowNotification}
                title={notificationDetails.title}
                desc={notificationDetails.desc}
                status={notificationDetails.status}
            />

            <div className='p-4'>
                <div className='flex justify-between'>
                    <form className='flex flex-col'>
                        <label className='text-gray-700'>
                            Filter By Part Or Vendor Control #<span className='text-gray-400'>(BAC...)</span>
                        </label>
                        <div className='flex flex-col items-center space-x-4 md:flex-row'>
                            <input
                                disabled={status.loading}
                                ref={partFilterRef}
                                type='text'
                                autoFocus
                                placeholder='Enter Part or Vendor Control Number'
                                className='p-2 w-72 rounded border disabled:opacity-50'
                            />
                            <button
                                type='submit'
                                disabled={status.loading || !currentUser.tableStructures}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePartFilter();
                                }}
                                title={!currentUser.tableStructures ? 'Loading...' : ''}
                                className='p-2 w-24 text-white rounded border cursor-pointer bg-blue-primary disabled:opacity-50'
                            >
                                Filter
                            </button>

                            <label className='flex items-center mt-2 text-sm md:mt-0'>
                                <input
                                    type='checkbox'
                                    className='mr-2'
                                    checked={showCompleted}
                                    onChange={(e) => setShowCompleted(e.target.checked)}
                                />
                                Show Completed
                            </label>
                        </div>
                    </form>
                    <div className='flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0'>
                        <button
                            type='submit'
                            disabled={status.loading || !currentUser.tableStructures}
                            title={!currentUser.tableStructures ? 'Loading...' : ''}
                            onClick={() => {
                                openReceivingLine();
                            }}
                            className='p-2 w-48 h-10 text-white rounded border cursor-pointer bg-blue-primary disabled:opacity-50'
                        >
                            Receive Inventory Line
                        </button>
                        <form className='flex flex-col'>
                            <select
                                disabled={status.loading}
                                className='p-2 w-48 rounded border disabled:opacity-50'
                                value={selectedMaid.Id || ''}
                                onChange={handleMaidChange}
                            >
                                <option value='' disabled>
                                    Select a Manifest
                                </option>
                                {allMaids &&
                                    allMaids.length &&
                                    !status.loading &&
                                    allMaids.map((maid) => {
                                        return (
                                            <option key={maid.Id} value={maid.Id}>
                                                {maid.Name}
                                            </option>
                                        );
                                    })}
                                {allMaids &&
                                    allMaids.filter((maid) => maid.Receiving_Allow_Multiple_Selection__c === 1).length >
                                        1 && <option value='multiple'>Multiple Manifests</option>}
                            </select>
                        </form>
                        <form className='flex flex-col'>
                            <select
                                disabled={status.loading}
                                className='p-2 w-48 rounded border disabled:opacity-50'
                                value={selectedScanner.name || ''}
                                onChange={handleScannerChange}
                            >
                                <option value='' disabled>
                                    Select a Scanner
                                </option>
                                {allScanners.length &&
                                    !status.loading &&
                                    allScanners.map((scanner) => {
                                        return (
                                            <option key={scanner.name} value={scanner.name}>
                                                {scanner.displayName}
                                            </option>
                                        );
                                    })}
                            </select>
                        </form>
                    </div>
                </div>
                {maidLines && showMaidLines && maidLines.manifestLines && !status.loading && (
                    <div className='relative z-10 mt-2 w-full overflow-x-auto bg-white border border-gray-300 shadow-sm'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='text-white bg-blue-primary'>
                                    <th className='px-2 py-2 border'>Manifest Number</th>
                                    <th className='px-2 py-2 border'>Manifest Line Number</th>
                                    <th className='px-2 py-2 border'>Owner Code</th>
                                    <th className='px-2 py-2 border'>Sub OC</th>
                                    <th className='px-2 py-2 border'>Part Number</th>
                                    <th className='px-2 py-2 border'>Part Description</th>
                                    <th className='px-2 py-2 border'>Status</th>
                                    <th className='px-2 py-2 border'>Vendor Control Number</th>
                                    <th className='px-2 py-2 border'>Serial Number</th>
                                    <th className='px-2 py-2 border'>Condition Code</th>
                                    <th className='px-2 py-2 border'>Qty Man</th>
                                    <th className='px-2 py-2 border'>Qty Rec</th>
                                    <th className='px-2 py-2 border'>Qty Scrapped</th>
                                    <th className='px-2 py-2 border'>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {maidLines.manifestLines
                                    .filter((line) => showCompleted || !isLineCompleted(line))
                                    .map((line) => (
                                        <tr
                                            key={line.Id}
                                            title={
                                                !line.isValidLine
                                                    ? `Not a valid manifest line - missing fields ${line.missingFields}`
                                                    : ''
                                            }
                                            className={`cursor-pointer ${
                                                !line.isValidLine ? 'bg-red-300' : 'hover:bg-gray-100'
                                            }`}
                                            onClick={() => {
                                                if (line.isValidLine) handleMaidLineSelection(line);
                                            }}
                                        >
                                            <td className='px-4 py-2 border'>{line.manifestName}</td>
                                            <td className='px-4 py-2 border'>{line.Name}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Owner_Code__rName}</td>
                                            <td className='px-4 py-2 border'>{line.XX_Sub_Owner_Code__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Part_Master__rName}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Keyword__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Status__c}</td>
                                            <td className='px-4 py-2 border'>{line.XX_Vendor_Control_No__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Serial_Number__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Condition_Code__rName}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Quantity_on_Manifest__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Quantity_Received__c}</td>
                                            <td className='px-4 py-2 border'>{line.inscor__Quantity_Scrapped__c}</td>
                                            <td className='px-4 py-2 border'>{line.CreatedDate.split('T')[0]}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}
